import { useState, useRef, useEffect, useMemo } from 'react'
import { serverNow } from '../lib/clock'
import { format } from 'date-fns'
import { Pause, Play, Trash2, Filter, ChevronDown, ChevronUp, Radio, ArrowRight, Clock, HeartPulse, Ban } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS, MAX_FEED } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { equipmentOf } from '../equipment'
import { useStopReasonLabel } from '../lib/catalogs'
import { holeTypeLabel } from '../lib/i18nText'
import { fmtDurShort } from '../i18n/format'

// Cor por TIPO de mensagem (igual à legenda). A linha é tingida de forma suave/fosca;
// o label do device fica neutro (branco). Strings literais p/ o Tailwind incluir as classes.
// Cada tipo tem o par claro E escuro: o registro era só de tema claro e no escuro o
// texto ficava em ~2,3:1 sobre a linha tingida.
const TYPE_STYLE = {
  telemetria:     { dot: 'bg-slate-400',   row: 'bg-slate-100/70 hover:bg-slate-100 dark:bg-slate-800/60 dark:hover:bg-slate-800',       text: 'text-slate-600 dark:text-slate-300',   icon: 'text-slate-400' },
  vmc_event:      { dot: 'bg-purple-500',  row: 'bg-purple-100/60 hover:bg-purple-100 dark:bg-purple-500/10 dark:hover:bg-purple-500/20', text: 'text-purple-700 dark:text-purple-300', icon: 'text-purple-600 dark:text-purple-400' },
  ping:           { dot: 'bg-cyan-500',    row: 'bg-cyan-100/60 hover:bg-cyan-100 dark:bg-cyan-500/10 dark:hover:bg-cyan-500/20',        text: 'text-cyan-700 dark:text-cyan-300',    icon: 'text-cyan-600 dark:text-cyan-400' },
  health:         { dot: 'bg-emerald-500', row: 'bg-emerald-100/60 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-600 dark:text-emerald-400' },
  reset:          { dot: 'bg-rose-500',    row: 'bg-rose-100/60 hover:bg-rose-100 dark:bg-rose-500/10 dark:hover:bg-rose-500/20',        text: 'text-rose-700 dark:text-rose-300',    icon: 'text-rose-600 dark:text-rose-400' },
  cleanUserCache: { dot: 'bg-orange-500',  row: 'bg-orange-100/60 hover:bg-orange-100 dark:bg-orange-500/10 dark:hover:bg-orange-500/20', text: 'text-orange-700 dark:text-orange-300', icon: 'text-orange-600 dark:text-orange-400' },
  webReply:       { dot: 'bg-indigo-500',  row: 'bg-indigo-100/60 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:hover:bg-indigo-500/20', text: 'text-indigo-700 dark:text-indigo-300', icon: 'text-indigo-600 dark:text-indigo-400' },
  update:         { dot: 'bg-teal-500',    row: 'bg-teal-100/60 hover:bg-teal-100 dark:bg-teal-500/10 dark:hover:bg-teal-500/20',        text: 'text-teal-700 dark:text-teal-300',    icon: 'text-teal-600 dark:text-teal-400' },
  forceLogoff:    { dot: 'bg-amber-500',   row: 'bg-amber-100/60 hover:bg-amber-100 dark:bg-amber-500/10 dark:hover:bg-amber-500/20',    text: 'text-amber-700 dark:text-amber-300',  icon: 'text-amber-600 dark:text-amber-400' },
  debug:          { dot: 'bg-fuchsia-500', row: 'bg-fuchsia-100/60 hover:bg-fuchsia-100 dark:bg-fuchsia-500/10 dark:hover:bg-fuchsia-500/20', text: 'text-fuchsia-700 dark:text-fuchsia-300', icon: 'text-fuchsia-600 dark:text-fuchsia-400' },
  geofence_alarm: { dot: 'bg-red-600',     row: 'bg-red-100/70 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20',            text: 'text-red-700 dark:text-red-300',      icon: 'text-red-600 dark:text-red-400' },
  // O sinal de vida chega a cada 30 s por device: linha sem tinta, texto apagado.
  vmc_health:     { dot: 'bg-lime-500',    row: 'bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800/60',                             text: 'text-slate-400 dark:text-slate-500',  icon: 'text-lime-500 dark:text-lime-400' },
  timesheet:      { dot: 'bg-yellow-500',  row: 'bg-yellow-100/60 hover:bg-yellow-100 dark:bg-yellow-500/10 dark:hover:bg-yellow-500/20', text: 'text-yellow-800 dark:text-yellow-300', icon: 'text-yellow-600 dark:text-yellow-400' },
  hole_abort:     { dot: 'bg-pink-500',    row: 'bg-pink-100/60 hover:bg-pink-100 dark:bg-pink-500/10 dark:hover:bg-pink-500/20',        text: 'text-pink-700 dark:text-pink-300',    icon: 'text-pink-600 dark:text-pink-400' },
}
const CMD_DEFAULT = { dot: 'bg-blue-400', row: 'bg-blue-100/60 hover:bg-blue-100 dark:bg-blue-500/10 dark:hover:bg-blue-500/20', text: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-600 dark:text-blue-400' }

// Label do device: neutro/branco — a cor agora vem da linha, pelo tipo.
const CHIP = 'text-xs font-medium px-1.5 py-0.5 rounded shrink-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'

// Legenda gerada do registry — sempre bate com a cor das linhas. É clicável: filtra o
// feed pelos tipos selecionados (toggle, multi). key = tipo retornado por typeOf().
const LEGEND = [
  { key: 'telemetria',     dot: TYPE_STYLE.telemetria.dot },
  { key: 'vmc_event',      dot: TYPE_STYLE.vmc_event.dot },
  { key: 'vmc_health',     dot: TYPE_STYLE.vmc_health.dot },
  { key: 'timesheet',      dot: TYPE_STYLE.timesheet.dot },
  { key: 'hole_abort',     dot: TYPE_STYLE.hole_abort.dot },
  { key: 'ping',           dot: TYPE_STYLE.ping.dot },
  { key: 'health',         dot: TYPE_STYLE.health.dot },
  { key: 'reset',          dot: TYPE_STYLE.reset.dot },
  { key: 'cleanUserCache', dot: TYPE_STYLE.cleanUserCache.dot },
  { key: 'webReply',       dot: TYPE_STYLE.webReply.dot },
  { key: 'update',         dot: TYPE_STYLE.update.dot },
  { key: 'forceLogoff',    dot: TYPE_STYLE.forceLogoff.dot },
  { key: 'debug',          dot: TYPE_STYLE.debug.dot },
  { key: 'geofence_alarm', dot: TYPE_STYLE.geofence_alarm.dot },
]

// Eventos VMC com tipo próprio na legenda. O sinal de vida do device (a cada 30 s) chama
// heartbeat na 0.8 e health no app 0.5.2 (sob a 0.7); este health é a NOTIFICAÇÃO, não o
// comando health que o web manda e que já tinha a chave 'health'.
const VMC_METHOD_TYPE = {
  heartbeat: 'vmc_health',
  health: 'vmc_health',
  timesheetStart: 'timesheet',
  timesheetEnd: 'timesheet',
  holeAbort: 'hole_abort',
}

// typeOf: tipo de uma mensagem do feed (mesma chave do TYPE_STYLE/LEGEND). As ações da
// tela de Debug chegam como "debug:<ação>" e colapsam num tipo só, senão filtrar a
// legenda para auditar ação destrutiva escondia justamente essas linhas.
export function typeOf(item) {
  if (item.commandType === 'geofence_alarm') return 'geofence_alarm'
  if (item.commandType === 'vmc_event') return VMC_METHOD_TYPE[item.method] || 'vmc_event'
  if (item.commandType === 'command') {
    return String(item.command || '').startsWith('debug:') ? 'debug' : item.command
  }
  return 'telemetria'
}

// O feed ordena pelo relógio do VMC (vmc.timestamp no corpo da mensagem). Dois eventos
// emitidos juntos podem chegar invertidos: a ingestão EdgeX → backend é concorrente e o
// received_at só diz quem entrou primeiro no servidor. O toggle no topo volta ao received_at.
export function feedTimeMs(item, key) {
  if (key === 'device' && item.deviceTs) return item.deviceTs
  return item.receivedAt ? new Date(item.receivedAt).getTime() : 0
}

function TimeCell({ item, timeKey }) {
  const ms = feedTimeMs(item, timeKey)
  const other = feedTimeMs(item, timeKey === 'device' ? 'received' : 'device')
  const fmt = (v) => (v ? format(new Date(v), 'HH:mm:ss.SSS') : '--:--:--')
  const fallback = timeKey === 'device' && !item.deviceTs
  return (
    <span
      title={`${timeKey === 'device' ? 'received_at' : 'vmc.timestamp'}: ${fmt(other)}`}
      className={`text-xs font-mono shrink-0 mt-0.5 w-28 ${fallback ? 'text-slate-300 dark:text-slate-600 italic' : 'text-slate-400 dark:text-slate-500'}`}
    >
      {fmt(ms)}
    </span>
  )
}

function RawButton({ open, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:text-slate-600 hover:border-slate-300 transition-colors font-mono shrink-0"
    >
      RAW {open ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
    </button>
  )
}

function RawBlock({ item }) {
  return (
    <pre className="mx-4 mt-2 mb-1 text-[10px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all max-h-96 w-auto">
      {JSON.stringify(item, null, 2)}
    </pre>
  )
}

function TelemetryItem({ item, label, timeKey }) {
  const telem = item.telemetry
  const [open, setOpen] = useState(false)

  return (
    <div className={`border-b border-slate-100 dark:border-slate-800 transition-colors py-2 ${TYPE_STYLE.telemetria.row}`}>
      <div className="flex gap-3 px-4 items-start">
        <TimeCell item={item} timeKey={timeKey} />
        <span className={CHIP}>{label}</span>
        <div className="flex-1 min-w-0">
          {telem ? (
            <div className="text-xs font-mono text-slate-600 dark:text-slate-300 flex flex-wrap gap-x-4 gap-y-0.5">
              {telem.latitude  != null && <span>lat <b>{telem.latitude.toFixed(6)}</b></span>}
              {telem.longitude != null && <span>lon <b>{telem.longitude.toFixed(6)}</b></span>}
              {telem.altitude_m  != null && <span>alt <b>{telem.altitude_m.toFixed(1)}m</b></span>}
              {telem.speed_kmh   != null && <span>spd <b>{telem.speed_kmh.toFixed(1)}km/h</b></span>}
              {telem.heading_deg != null && <span>hdg <b>{telem.heading_deg.toFixed(1)}°</b></span>}
            </div>
          ) : (
            <pre className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {JSON.stringify(item.raw?.readings?.[0] ?? item.raw)}
            </pre>
          )}
        </div>
        <RawButton open={open} onToggle={() => setOpen(o => !o)} />
      </div>
      {open && <RawBlock item={item} />}
    </div>
  )
}

function paramList(params) {
  if (Array.isArray(params)) return params
  if (typeof params === 'string') {
    try {
      const v = JSON.parse(params)
      return Array.isArray(v) ? v : []
    } catch { return [] }
  }
  return []
}

const pStr = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '')

// timesheetStart: [userId, vehicleCode, balde, motivo, startUnix, reasonCode]. O motivo
// sai traduzido pelo código do catálogo de paragem; sem ele, o texto que o device mandou.
function TimesheetStartSummary({ params }) {
  const { t, i18n } = useTranslation()
  const reasonLabel = useStopReasonLabel()
  const arr = paramList(params)
  const bucket = pStr(arr[2])
  const code = pStr(arr[5]) || pStr(arr[3])
  const bucketKey = `admin:production.buckets.${bucket.toLowerCase()}`
  const bucketLabel = bucket && i18n.exists(bucketKey) ? t(bucketKey) : bucket
  const reason = reasonLabel(code, pStr(arr[3]))
  const text = [bucketLabel, reason].filter(Boolean).join(': ')
  return text ? <span className="font-normal truncate">· {text}</span> : null
}

// timesheetEnd: [userId, vehicleCode, startUnix, endUnix].
function TimesheetEndSummary({ params }) {
  const { t } = useTranslation()
  const arr = paramList(params)
  const start = Number(arr[2]), end = Number(arr[3])
  if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end < start) return null
  return <span className="font-normal truncate">· {t('ops:feed.stop_lasted', { duration: fmtDurShort(end - start) })}</span>
}

// holeAbort: [userId, vehicleCode, holeId, holeType].
function HoleAbortSummary({ params }) {
  const { i18n } = useTranslation()
  const arr = paramList(params)
  const text = [pStr(arr[2]), holeTypeLabel(pStr(arr[3]), i18n.language)].filter(Boolean).join(' · ')
  return text ? <span className="font-normal truncate">· {text}</span> : null
}

function VmcEventSummary({ item }) {
  if (item.method === 'timesheetStart') return <TimesheetStartSummary params={item.params} />
  if (item.method === 'timesheetEnd') return <TimesheetEndSummary params={item.params} />
  if (item.method === 'holeAbort') return <HoleAbortSummary params={item.params} />
  return null
}

const VMC_ICON = { vmc_health: HeartPulse, hole_abort: Ban }

// Evento do VMC (não-telemetria): login, checklist, produção, registros, paragem…
// No estilo do ping: nome do VMC + cor do tipo + nome do evento e, para os eventos
// do 0.8 que o despachante precisa ler, um resumo dos params; payload só no RAW.
function VmcEventItem({ item, label, timeKey }) {
  const [open, setOpen] = useState(false)
  const type = typeOf(item)
  const s = TYPE_STYLE[type] || TYPE_STYLE.vmc_event
  const Icon = VMC_ICON[type] || Radio
  const quiet = type === 'vmc_health'
  return (
    <div className={`border-b border-slate-100 dark:border-slate-800 transition-colors ${quiet ? 'py-1' : 'py-2'} ${s.row}`}>
      <div className="flex gap-3 px-4 items-start">
        <TimeCell item={item} timeKey={timeKey} />
        <span className={CHIP}>{label}</span>
        <div className={`flex items-center gap-1.5 text-xs flex-1 min-w-0 ${quiet ? 'font-normal' : 'font-medium'} ${s.text}`}>
          <Icon size={11} className={`shrink-0 ${s.icon}`} />
          <span className="shrink-0">{item.method}</span>
          <VmcEventSummary item={item} />
        </div>
        <RawButton open={open} onToggle={() => setOpen(o => !o)} />
      </div>
      {open && <RawBlock item={item} />}
    </div>
  )
}

// RAW de comando: 2 áreas — payload enviado | resposta da API command.
function CommandRawBlock({ item }) {
  const { t } = useTranslation()
  const box = 'text-[10px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all max-h-96'
  return (
    <div className="mx-4 mt-2 mb-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">{t('ops:feed.payload_sent')}</p>
        <pre className={box}>{JSON.stringify(item.sent ?? {}, null, 2)}</pre>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">{t('ops:feed.response_api')}</p>
        <pre className={box}>{JSON.stringify(item.response ?? {}, null, 2)}</pre>
      </div>
    </div>
  )
}

// Comando enviado pelo web ao device. Estilo do ping: nome do VMC + cor própria +
// nome do comando; detalhe/payload só no RAW (enviado | resposta).
function CommandItem({ item, label, timeKey }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const s = TYPE_STYLE[typeOf(item)] || CMD_DEFAULT
  return (
    <div className={`border-b border-slate-100 dark:border-slate-800 transition-colors py-2 ${s.row}`}>
      <div className="flex gap-3 px-4 items-start">
        <TimeCell item={item} timeKey={timeKey} />
        <span className={CHIP}>{label}</span>
        <div className={`flex items-center gap-1.5 text-xs font-medium flex-1 min-w-0 ${s.text}`}>
          <ArrowRight size={11} className={`shrink-0 ${s.icon}`} />
          {item.command}
          {item.detail && <span className="font-mono text-slate-500 dark:text-slate-400">· {item.detail}</span>}
          {item.ok === false && <span className="text-status-down">· {t('ops:feed.failed')}</span>}
        </div>
        <RawButton open={open} onToggle={() => setOpen(o => !o)} />
      </div>
      {open && <CommandRawBlock item={item} />}
    </div>
  )
}

function GeofenceAlarmItem({ item, label, timeKey }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`border-b border-slate-100 dark:border-slate-800 transition-colors py-2 ${TYPE_STYLE.geofence_alarm.row}`}>
      <div className="flex gap-3 px-4 items-start">
        <TimeCell item={item} timeKey={timeKey} />
        <span className={CHIP}>{label}</span>
        <div className={`flex items-center gap-1.5 text-xs font-medium flex-1 min-w-0 ${TYPE_STYLE.geofence_alarm.text}`}>
          <Radio size={11} className={`shrink-0 ${TYPE_STYLE.geofence_alarm.icon}`} />
          {item.message || `${item.alarmType} · ${item.geofenceName}`}
        </div>
        <RawButton open={open} onToggle={() => setOpen(o => !o)} />
      </div>
      {open && <RawBlock item={item} />}
    </div>
  )
}

export function FeedItem({ item, label, timeKey = 'received' }) {
  const p = { item, label, timeKey }
  if (item.commandType === 'geofence_alarm') return <GeofenceAlarmItem {...p} />
  if (item.commandType === 'vmc_event') return <VmcEventItem {...p} />
  if (item.commandType === 'command')   return <CommandItem {...p} />
  return <TelemetryItem {...p} />
}

export default function LiveFeedView({ visible: _visible }) {
  const { t } = useTranslation()
  const { feed, feedDropped, resetFeedWindow, connected, devices, categories, mines, feedPaused, setFeedPaused } = useWS()
  const { mineScope, matchesScope } = useScope()
  // Índice nome→device: o nameOf roda por linha (até 120), e o equipmentOf varre o
  // cadastro; sem o índice era uma varredura por linha por frame.
  const deviceByName = useMemo(() => {
    const m = new Map()
    for (const d of devices) m.set(d.name, d)
    return m
  }, [devices])
  const nameOf = (dn) => {
    const d = deviceByName.get(dn)
    return (d && equipmentOf(d, categories, mines).name) || dn
  }
  // Nasce AO VIVO (play habilitado). A pausa é do stream do servidor e vale para o
  // navegador inteiro, então o estado mora no WSContext: abrir os Detalhes avançados
  // pausa de verdade e este botão tem que dizer isso.
  const paused = feedPaused
  const [filter, setFilter] = useState('')
  const [types, setTypes] = useState(() => new Set())
  // Ligado por padrão: o sinal de vida é uma linha a cada 30 s por device.
  const [hideHealth, setHideHealth] = useState(true)
  const toggleType = (key) => {
    // Escolher o sinal de vida na legenda com ele escondido mostraria uma lista vazia.
    if (key === 'vmc_health' && !types.has(key)) setHideHealth(false)
    setTypes(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }
  const [clearedAt, setClearedAt] = useState(null)
  // Ordenação: 'device' = vmc.timestamp do corpo da mensagem (padrão), 'received' = chegada no servidor.
  const [sortBy, setSortBy] = useState('device')
  // Congela o feed no momento da pausa; null = ao vivo.
  const [pausedSnapshot, setPausedSnapshot] = useState(null)
  const containerRef = useRef(null)

  // A pausa pode vir de fora (Detalhes avançados): congela o que estava na tela.
  useEffect(() => {
    setPausedSnapshot(prev => (feedPaused ? (prev ?? [...feed]) : null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedPaused])

  const displayed = paused ? (pausedSnapshot ?? feed) : feed

  // Nomes dos devices dentro do escopo de mina global (linhas de fora saem do feed).
  const scopedNames = new Set(devices.filter(matchesScope).map(d => d.name))

  let hiddenHealth = 0
  const filtered = displayed.filter(m => {
    if (mineScope !== 'all' && m.deviceName && !scopedNames.has(m.deviceName)) return false
    const type = typeOf(m)
    if (types.size > 0 && !types.has(type)) return false
    if (filter && !`${m.deviceName ?? ''} ${nameOf(m.deviceName)}`.toLowerCase().includes(filter.toLowerCase())) return false
    if (clearedAt && new Date(m.receivedAt).getTime() <= clearedAt) return false
    if (hideHealth && type === 'vmc_health') { hiddenHealth += 1; return false }
    return true
  })

  const sorted = [...filtered].sort((a, b) => feedTimeMs(b, sortBy) - feedTimeMs(a, sortBy))

  const handlePause = () => setFeedPaused(!paused)

  const handleClear = () => {
    // O corte é comparado com o receivedAt, que é carimbado pelo SERVIDOR: marcá-lo
    // com o relógio da estação fazia o Limpar não limpar nada numa estação atrasada,
    // e apagar mensagens que ainda iam chegar numa adiantada.
    setClearedAt(serverNow())
    resetFeedWindow()
  }

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('ops:feed.title')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('ops:feed.shown', { count: sorted.length })} {clearedAt && `· ${t('ops:feed.filtered_since', { time: format(new Date(clearedAt), 'HH:mm:ss') })}`}
            </p>
            {/* O teto de 120 é aplicado ANTES de qualquer filtro: uma busca sem
                resultado pode ser história que já saiu da janela, não ausência de dado. */}
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              {feedDropped > 0
                ? t('ops:feed.window_dropped', { max: MAX_FEED, dropped: feedDropped })
                : t('ops:feed.window_cap', { max: MAX_FEED })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* O selo só olhava a conexão e dizia "ao vivo" com o stream pausado. */}
            <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
              paused ? 'bg-status-stop/15 text-status-stop'
                : connected ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${paused ? 'bg-status-stop' : connected ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
              {paused ? t('ops:feed.paused_badge') : connected ? t('ops:feed.live') : t('ops:feed.disconnected')}
            </div>
            <label title={t('ops:feed.hide_health_hint')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer select-none">
              <input type="checkbox" checked={hideHealth} onChange={e => setHideHealth(e.target.checked)} className="accent-[#017f96]" />
              <HeartPulse size={12} className={TYPE_STYLE.vmc_health.icon} />
              {t('ops:feed.hide_health')}
              {hideHealth && hiddenHealth > 0 && (
                <span className="text-slate-400 dark:text-slate-500 tabular-nums">({t('ops:feed.hidden_count', { count: hiddenHealth })})</span>
              )}
            </label>
            <button
              onClick={() => setSortBy(k => (k === 'device' ? 'received' : 'device'))}
              title={t('ops:feed.sort_hint')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Clock size={12} className="text-slate-400 dark:text-slate-500" />
              {sortBy === 'device' ? t('ops:feed.sort_device') : t('ops:feed.sort_received')}
            </button>
            <button
              onClick={handlePause}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              {paused ? <><Play size={12} /> {t('common:actions.resume')}</> : <><Pause size={12} /> {t('common:actions.pause')}</>}
            </button>
            <button
              onClick={handleClear}
              title={t('ops:feed.clear_hint')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
            >
              <Trash2 size={12} /> {t('common:actions.clear')}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative max-w-xs flex-1 min-w-[12rem]">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder={t('ops:feed.filter_placeholder')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
          <div className="flex items-center gap-x-1.5 gap-y-1 text-xs flex-1 min-w-0 flex-wrap">
            {LEGEND.map(({ key, dot }) => {
              const label = t(`ops:feed.legend.${key}`)
              const active = types.has(key)
              const cls = active
                ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 ring-1 ring-slate-300'
                : types.size > 0 ? 'text-slate-300 dark:text-slate-600 hover:text-slate-500' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600'
              return (
                <button key={key} onClick={() => toggleType(key)} title={t('ops:feed.filter_by', { type: label })}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${cls}`}>
                  <span className={`w-2 h-2 rounded-full ${dot} inline-block`} /> {label}
                </button>
              )
            })}
            {types.size > 0 && (
              <button onClick={() => setTypes(new Set())} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 underline ml-1">{t('ops:feed.clear_selection')}</button>
            )}
          </div>
        </div>
      </div>

      {paused && (
        <div className="flex items-center gap-2 px-6 py-2 text-xs bg-status-stop/10 text-status-stop border-b border-status-stop/20">
          <Pause size={12} className="shrink-0" />
          <span>{t('ops:feed.paused_notice')}</span>
          <button onClick={() => setFeedPaused(false)} className="underline font-medium">{t('common:actions.resume')}</button>
        </div>
      )}

      <div ref={containerRef} className="flex-1 overflow-y-auto scrollbar-thin bg-white dark:bg-slate-900">
        {sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400 dark:text-slate-500">
            <p className="text-sm">{displayed.length === 0 ? t('ops:feed.waiting') : t('ops:feed.none_matching')}</p>
          </div>
        ) : (
          sorted.map((item, i) => <FeedItem key={item.feedKey ?? item.id ?? `cmd-${i}`} item={item} label={nameOf(item.deviceName)} timeKey={sortBy} />)
        )}
      </div>
    </div>
  )
}
